/**
 * Decide whether a bot that left the room unexpectedly gets respawned.
 *
 * The visitor stays in the Daily room; the widget asks /voice/token for a
 * fresh bot into the same room (`rejoinRoom` + `respawnBot`). Bounded so a
 * bot that crashes on boot cannot loop: two respawns per call, and only
 * while the room has life left. A bot that ended the call on purpose
 * (idle timeout, goodbye spoken) is never respawned.
 */

import type { RejoinRoom } from './daily-session';

export const RESPAWN_MAX_ATTEMPTS = 2;
/** How long the new bot gets to join before the call is declared lost. */
export const RESPAWN_JOIN_TIMEOUT_MS = 20_000;
/** A room this close to expiry is not worth a respawn. */
const ROOM_MIN_LIFE_SEC = 90;

export function shouldRespawnBot(input: {
  intentional: boolean;
  attempts: number;
  room: RejoinRoom | null;
  nowSec: number;
}): boolean {
  if (input.intentional) return false;
  if (input.attempts >= RESPAWN_MAX_ATTEMPTS) return false;
  if (!input.room || !input.room.sig) return false;
  return input.room.expiresAt > input.nowSec + ROOM_MIN_LIFE_SEC;
}

/**
 * chat-api's drop signal (`voice-bot-dropped`): the spawn server saw the
 * bot process die. Handled by the voice bootstrap, never rendered.
 */
export function isBotDroppedEvent(event: { type: string }): boolean {
  return event.type === 'voice-bot-dropped';
}
